import { useState } from "react";
import ReusableTable from "../../../Reuseable/ReusableTable/ReusableTable";
import "./BucketwiseTable.scss";
import "../../../components/AgencyComp/UserComp/UserList.scss";
import { Flex, Input, Dropdown, Menu, Button, Skeleton } from "antd";
import { ReactComponent as SearchICon } from "../../../assets/Icons/SearchIcon.svg";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import { ReactComponent as ExportICon } from "../../../assets/Icons/exportIcon.svg";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { dummyResponse } from "./data";
import { formatCurrency, formatWithCommas } from "../../../assets/helpers";
import { useSelector } from "react-redux";
import { exportBucketAllocationFile } from "../../../assets/api/othres";

const allColumns = [
  { title: "DPD", dataIndex: "dpd", key: "dpd" },
  { title: "Bucket", dataIndex: "bucket", key: "bucket" },
  { title: "Resolved", dataIndex: "resolved", key: "resolved" },
  { title: "OD/POS", dataIndex: "odpos", key: "odpos" },
  { title: "POS", dataIndex: "pos", key: "pos" },
  { title: "Total Count", dataIndex: "totalCount", key: "totalCount" },
  { title: "Total POS", dataIndex: "totalPos", key: "totalPos" },
  { title: "Total OD", dataIndex: "totalOd", key: "totalOd" },
];

const BucketwiseTable = ({ data, loading, id }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { token } = useSelector((state) => state.auth);
  const [loadingExport, setLoadingExport] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.key)
  );
  const { format } = useSelector(state => state.priceFormat);
  const [hiddenColumns, setHiddenColumns] = useState([]);



  const bucketwiseData = data?.map((item, index) => ({
    key: index,
    cases: item.cases,
    agingBucket: item.bucket,
    odposValue: item.resolved.odpos.value,
    odposPerc: item.resolved.odpos.percentage,
    posValue: item.resolved.pos.value,
    posPerc: item.resolved.pos.percentage,
    totalOdposNos: item.total_odpos.numbers,
    totalOdposScore: item.total_odpos.crore,
    totalPosNos: item.total_pos.numbers,
    totalPosScore: item.total_pos.crore,
  }));



  // Build columns for table, adding cross icon to each
  const columns = [
    {
      title: "Cases",
      dataIndex: "cases",
      key: "cases",
      render: (text) => loading ? <Skeleton.Input size="small" active /> : formatWithCommas(text) || '0'
    },
    {
      title: "Aging Bucket",
      dataIndex: "agingBucket",
      key: "agingBucket",
      render: (text) => loading ? <Skeleton.Input size="small" active /> : text || '0'

    },
    {
      title: "Resolved",
      children: [
        {
          title: "ODPOS",
          children: [
            {
              title: "Value", dataIndex: "odposValue", key: "odposValue", render: (text) => loading ? <Skeleton.Input size="small" active /> : formatWithCommas(text) || '0'
            },
            {
              title: "Percentage", dataIndex: "odposPerc", key: "odposPerc", render: (text) => loading ? <Skeleton.Input size="small" active /> : text || '0'
            },
          ],
        },
        {
          title: "POS",
          children: [
            {
              title: "Value", dataIndex: "posValue", key: "posValue", render: (text) => loading ? <Skeleton.Input size="small" active /> : formatWithCommas(text) || '0'
            },
            {
              title: "Percentage", dataIndex: "posPerc", key: "posPerc", render: (text) => loading ? <Skeleton.Input size="small" active /> : text || '0'
            },
          ],
        },
      ],
    },
    {
      title: "Total ODPOS",
      children: [
        {
          title: "Nos", dataIndex: "totalOdposNos", key: "totalOdposNos", render: (text) => loading ? <Skeleton.Input size="small" active /> : formatWithCommas(text) || '0'
        },
        {
          title: "Crore", dataIndex: "totalOdposScore", key: "totalOdposScore", render: (text) => loading ? <Skeleton.Input size="small" active /> : formatCurrency(text, format) || '0'
        },
      ],
    },
    {
      title: "Total POS",
      children: [
        {
          title: "Nos", dataIndex: "totalPosNos", key: "totalPosNos", render: (text) => loading ? <Skeleton.Input size="small" active /> : formatWithCommas(text) || '0'
        },
        {
          title: "Crore", dataIndex: "totalPosScore", key: "totalPosScore", render: (text) => loading ? <Skeleton.Input size="small" active /> : formatCurrency(text, format) || '0'
        },
      ],
    },
  ];



  // Add dropdown at the end for hidden columns
  if (hiddenColumns?.length > 0) {
    columns.push({
      title: (
        <Dropdown
          overlay={
            <Menu>
              {hiddenColumns.map((key) => {
                const col = allColumns.find((c) => c.key === key);
                return (
                  <Menu.Item
                    key={key}
                    onClick={() => {
                      setVisibleColumns([...visibleColumns, key]);
                      setHiddenColumns(hiddenColumns.filter((k) => k !== key));
                    }}
                  >
                    <PlusOutlined style={{ marginRight: 4 }} />
                    {col.title}
                  </Menu.Item>
                );
              })}
            </Menu>
          }
          trigger={["click"]}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#3070F0", // blue
              color: "#fff",
              cursor: "pointer",
              fontSize: 18,
              boxShadow: "0 1px 4px rgba(48,112,240,0.15)",
            }}
          >
            <PlusOutlined />
          </span>
        </Dropdown>
      ),
      dataIndex: "__addColumn__",
      key: "__addColumn__",
      render: () => null,
      sorter: false,
      filterDropdown: null,
    });
  }

  const handleExportFile = async () => {
    try {
      setLoadingExport(true);
      const res = await exportBucketAllocationFile(id, token);

      if(res?.status === 200) {
        setLoadingExport(false);
        const url = res?.data?.data?.download_url;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `bucketwise_allocation_${id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
setLoadingExport(false);
    }
  }

  return (
    <Flex className="bucketwiseContainer">
      <Flex
        className="bucketwise-header"
        justify="space-between"
        align="center"
      >
        <Input
          placeholder="Search"
          allowClear
          className="searchInput"
          prefix={<SearchICon />}
          style={{visibility:'hidden'}}
        />

        <Flex className="exportButton">
          <NormalButton type="primary" loading={loadingExport} suffixIcon={<ExportICon />} onClick={handleExportFile}>
            Export
          </NormalButton>
        </Flex>
      </Flex>
      <ReusableTable
        columns={columns}
        dataSource={loading ? Array(8).fill({}) : bucketwiseData}
        rowKey="key"
        bordered
        pageSize={pageSize}
        CurrentPageNo={currentPage}
        // totalItems={bucketwiseData.length}
        setPageSize={setPageSize}
        triggerChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
        className="bucketwiseTable"

      />
    </Flex>
  );
};

export default BucketwiseTable;
